import { fmtDate, fmtDateTime } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, User, Hammer, Pencil, Save, X, Loader2, Link2, ClipboardCheck, CheckCheck } from "lucide-react";
import { api, API_ORIGIN } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState } from "@/components/States";
import { NumberPlate } from "@/components/NumberPlate";
import { StatusChip } from "@/components/StatusChip";
import { RelatedExpenses } from "@/components/RelatedExpenses";
import { CustomerPicker } from "@/components/CustomerPicker";
import { VehicleTimeline } from "@/components/VehicleTimeline";
import { LogCallModal } from "@/components/LogCallModal";
import { Phone } from "lucide-react";
import { WhatsAppSendDialog, WhatsAppButton } from "@/components/WhatsAppSendDialog";
import { WhatsAppLog } from "@/components/WhatsAppLog";
import { waVehicleReadyText } from "@/lib/whatsapp";
import { toast } from "sonner";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState({});
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [pickCustomer, setPickCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waRefresh, setWaRefresh] = useState(0);
  const [callOpen, setCallOpen] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const v = await api(`/vehicles/${id}`);
      setVehicle(v);
      if (v.customer_id) api(`/customers/${v.customer_id}`).then(setCustomer).catch(() => setCustomer(null));
      else setCustomer(null);
      api(`/workshop/vehicles/${id}/history`).then((h) => setHistory(h && typeof h === "object" ? h : {})).catch(() => {});
      api(`/vehicles/${id}/summary`).then(setAccount).catch(() => setAccount(null));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const startEdit = () => {
    setForm({
      make: vehicle.make || "", model: vehicle.model || "", year: vehicle.year ?? "", colour: vehicle.colour || "",
      fuel_type: vehicle.fuel_type || "", engine_capacity: vehicle.engine_capacity ?? "",
      mot_expiry: vehicle.mot_expiry || "", tax_expiry: vehicle.tax_expiry || "",
      vin: vehicle.vin || "", next_service_date: vehicle.next_service_date || "", next_service_mileage: vehicle.next_service_mileage ?? "",
    });
    setPickCustomer(customer);
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!form.make.trim() || !form.model.trim()) { toast.error("Make and model are required"); return; }
    setSaving(true);
    try {
      await api(`/vehicles/${id}`, { method: "PATCH", body: JSON.stringify({
        make: form.make.trim(), model: form.model.trim(),
        year: form.year ? Number(form.year) : null, colour: form.colour || null, fuel_type: form.fuel_type || null,
        engine_capacity: form.engine_capacity ? Number(form.engine_capacity) : null,
        mot_expiry: form.mot_expiry || null, tax_expiry: form.tax_expiry || null,
        vin: form.vin?.trim() || null,
        next_service_date: form.next_service_date || null,
        next_service_mileage: form.next_service_mileage ? Number(form.next_service_mileage) : null,
        customer_id: pickCustomer?.id || null,
      }) });
      toast.success("Vehicle updated"); setEditing(false); load();
    } catch (e) { toast.error(e.message || "Could not update vehicle"); } finally { setSaving(false); }
  };

  const remove = async () => { if (!window.confirm("Delete this vehicle?")) return; try { await api(`/vehicles/${id}`, { method: "DELETE" }); toast.success("Vehicle deleted"); navigate("/vehicles"); } catch (e) { toast.error(e.message); } };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const metrics = [["Year", vehicle.year], ["Colour", vehicle.colour], ["Fuel", vehicle.fuel_type], ["Engine", vehicle.engine_capacity ? `${vehicle.engine_capacity}cc` : null], ["MOT expiry", vehicle.mot_expiry], ["Tax due", vehicle.tax_expiry]];

  return (
    <div>
      <Link to="/vehicles" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Vehicles</Link>
      <PageHeader title={`${vehicle.make} ${vehicle.model}`}
        actions={<>
          {!editing && <button onClick={startEdit} data-testid="edit-vehicle-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Pencil className="h-4 w-4" />Edit</button>}
          <button onClick={() => navigate(`/jobs/new?vehicle_id=${id}`)} data-testid="create-job-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white"><Plus className="h-4 w-4" />Create Job</button>
          <button onClick={() => setCallOpen(true)} data-testid="log-call-btn" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 text-sm font-bold text-midnight hover:bg-secondary"><Phone className="h-4 w-4" />Log Call</button>
          {customer && <WhatsAppButton onClick={() => setWaOpen(true)} label="Vehicle Ready" testId="vehicle-whatsapp-btn" />}
          <button onClick={remove} data-testid="delete-vehicle-btn" className="rounded-lg border border-danger/30 bg-danger-bg px-3 py-2.5 text-sm font-bold text-danger"><Trash2 className="h-4 w-4" /></button>
        </>} />

      <div className="mb-4 flex items-center gap-4 rounded-xl border border-border bg-card p-5"><NumberPlate vrm={vehicle.vrm} size="lg" /></div>

      {!editing && <VehicleTimeline vehicleId={id} />}

      {editing ? (
        <div className="mb-4 rounded-xl border border-border bg-card p-5">
          <h3 className="mb-3 font-display text-lg font-bold uppercase text-midnight">Edit Vehicle</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <VF label="MAKE *" v={form.make} on={(x) => setForm({ ...form, make: x })} t="edit-veh-make" />
            <VF label="MODEL *" v={form.model} on={(x) => setForm({ ...form, model: x })} t="edit-veh-model" />
            <VF label="YEAR" v={form.year} on={(x) => setForm({ ...form, year: x })} t="edit-veh-year" />
            <VF label="COLOUR" v={form.colour} on={(x) => setForm({ ...form, colour: x })} t="edit-veh-colour" />
            <VF label="FUEL" v={form.fuel_type} on={(x) => setForm({ ...form, fuel_type: x })} t="edit-veh-fuel" />
            <VF label="ENGINE (CC)" v={form.engine_capacity} on={(x) => setForm({ ...form, engine_capacity: x })} t="edit-veh-engine" />
            <VF label="MOT EXPIRY" v={form.mot_expiry} on={(x) => setForm({ ...form, mot_expiry: x })} t="edit-veh-mot" />
            <VF label="TAX DUE" v={form.tax_expiry} on={(x) => setForm({ ...form, tax_expiry: x })} t="edit-veh-tax" />
            <VF label="VIN" v={form.vin} on={(x) => setForm({ ...form, vin: x })} t="edit-veh-vin" />
            <VF label="NEXT SERVICE DATE" v={form.next_service_date} on={(x) => setForm({ ...form, next_service_date: x })} t="edit-veh-nsd" />
            <VF label="NEXT SERVICE (MI)" v={form.next_service_mileage} on={(x) => setForm({ ...form, next_service_mileage: x })} t="edit-veh-nsm" />
          </div>
          <div className="mt-4">
            <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold tracking-widest text-muted-foreground"><Link2 className="h-3.5 w-3.5" />LINKED CUSTOMER</span>
            <CustomerPicker value={pickCustomer} onChange={setPickCustomer} />
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={saveEdit} disabled={saving} data-testid="save-vehicle-btn" className="flex items-center justify-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-extrabold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Save</button>
            <button onClick={() => setEditing(false)} data-testid="cancel-vehicle-btn" className="flex items-center gap-1 rounded-lg border border-border px-4 py-2.5 text-sm font-bold text-midnight"><X className="h-4 w-4" />Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {metrics.filter(([, v]) => v).map(([label, value]) => (
              <div key={label} className="rounded-xl border border-border bg-card p-4"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p><p className="mt-1 font-display text-lg font-bold text-midnight">{value}</p></div>
            ))}
          </div>
          {customer ? (
            <button onClick={() => navigate(`/customers/${customer.id}`)} data-testid="vehicle-customer-card" className="mb-4 flex w-full items-center gap-3 rounded-xl border border-border bg-card p-4 text-left hover:border-brand">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-tertiary text-brand"><User className="h-5 w-5" /></div>
              <div className="flex-1"><p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Owner</p><p className="font-bold text-midnight">{customer.name}</p></div>
            </button>
          ) : (
            <button onClick={startEdit} data-testid="link-customer-prompt" className="mb-4 flex w-full items-center gap-3 rounded-xl border border-dashed border-border bg-card p-4 text-left hover:border-brand">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Link2 className="h-5 w-5" /></div>
              <div className="flex-1"><p className="font-bold text-midnight">No customer linked</p><p className="text-xs text-muted-foreground">Tap Edit to assign an owner to this vehicle.</p></div>
            </button>
          )}
        </>
      )}

      <h2 className="mb-3 font-display text-xl font-bold uppercase text-midnight">Service History</h2>
      {(history.jobs || []).length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No service history yet.</p>
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {(history.jobs || []).map((j) => (
            <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-secondary">
              <Hammer className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-midnight">{j.title}</p><p className="text-xs text-muted-foreground">{fmtDate(j.created_at)}</p></div>
              {j.status && <StatusChip status={j.status} />}
            </button>
          ))}
        </div>
      )}

      {(history.check_ins || []).length > 0 && (
        <>
          <h2 className="mb-3 mt-6 flex items-center gap-2 font-display text-xl font-bold uppercase text-midnight"><ClipboardCheck className="h-5 w-5 text-brand-2" />Check-Ins</h2>
          <div className="space-y-2">
            {history.check_ins.map((ci) => <ConditionCard key={ci.id} rec={ci} kind="checkin" />)}
          </div>
        </>
      )}

      {(history.handovers || []).length > 0 && (
        <>
          <h2 className="mb-3 mt-6 flex items-center gap-2 font-display text-xl font-bold uppercase text-midnight"><CheckCheck className="h-5 w-5 text-success" />Handovers</h2>
          <div className="space-y-2">
            {history.handovers.map((h) => <ConditionCard key={h.id} rec={h} kind="handover" />)}
          </div>
        </>
      )}

      {account && (account.invoices?.length > 0 || account.payments?.length > 0) && (
        <>
          <h2 className="mb-3 mt-6 flex items-center gap-2 font-display text-xl font-bold uppercase text-midnight"><User className="h-5 w-5 text-brand" />Account</h2>
          <div className="rounded-xl border border-border bg-card p-5" data-testid="vehicle-account">
            <div className="mb-3 flex gap-6 text-sm">
              <div><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Invoiced</p><p className="font-display text-lg font-bold text-midnight">£{(account.totals?.invoiced || 0).toFixed(2)}</p></div>
              <div><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Paid</p><p className="font-display text-lg font-bold text-success">£{(account.totals?.paid || 0).toFixed(2)}</p></div>
              <div><p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Outstanding</p><p className="font-display text-lg font-bold text-danger" data-testid="vehicle-outstanding">£{(account.totals?.outstanding || 0).toFixed(2)}</p></div>
            </div>
            {account.invoices?.length > 0 && (
              <div className="divide-y divide-border border-t border-border">
                {account.invoices.map((inv) => (
                  <Link key={inv.id} to={`/invoice/${inv.id}`} data-testid={`vehicle-inv-${inv.id}`} className="flex items-center justify-between py-2.5 text-sm hover:bg-secondary">
                    <span className="font-bold text-midnight">{inv.invoice_number}</span>
                    <span className="text-xs text-muted-foreground">£{Number(inv.total_gross || 0).toFixed(2)} · Due £{Number(inv.outstanding_amount || 0).toFixed(2)}</span>
                    <StatusChip status={inv.display_status || "unpaid"} />
                  </Link>
                ))}
              </div>
            )}
            {account.payments?.length > 0 && (
              <div className="mt-3 border-t border-border pt-3">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Payment history</p>
                {account.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`vehicle-payment-${p.id}`}>
                    <span className="font-bold text-midnight">£{(p.amount * (p.is_refund ? -1 : 1)).toFixed(2)}</span>
                    <span className="text-xs uppercase text-muted-foreground">{(p.method || "").replace("_", " ")}</span>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(p.paid_at || p.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {account?.fitted_parts?.length > 0 && (
        <div className="mt-6" data-testid="vehicle-fitted-parts">
          <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold uppercase text-midnight"><Hammer className="h-5 w-5 text-brand" />Fitted parts</h2>
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {account.fitted_parts.map((fp, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`fitted-part-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-midnight">{fp.qty}× {fp.description}</p>
                  {fp.job_id && <Link to={`/jobs/${fp.job_id}`} className="truncate text-xs font-semibold text-brand-2">{fp.job_title || "Job"}</Link>}
                </div>
                <span className="text-xs text-muted-foreground">{fmtDate(fp.date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <RelatedExpenses vehicleId={id} title="Expenses for this vehicle" />
      <WhatsAppLog entityType="vehicle" entityId={id} refreshKey={waRefresh} />
      <WhatsAppSendDialog
        open={waOpen} onClose={() => setWaOpen(false)} onSent={() => setWaRefresh((x) => x + 1)}
        customerId={customer?.id} customerName={customer?.name} to={customer?.phone}
        defaultText={waVehicleReadyText({ customerName: customer?.name, vrm: vehicle.vrm })}
        kind="vehicle_ready" entityType="vehicle" entityId={id}
      />
      <LogCallModal
        open={callOpen} onClose={() => setCallOpen(false)}
        customerId={customer?.id} vehicleId={id} customerName={customer?.name} vrm={vehicle.vrm}
      />
    </div>
  );
}

function ConditionCard({ rec, kind }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`${kind}-record-${rec.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="font-bold text-midnight">{fmtDateTime(rec.created_at)}</span>
        {rec.mileage != null && <span className="text-muted-foreground">Mileage: <b className="text-midnight">{rec.mileage}</b></span>}
        {rec.mileage_out != null && <span className="text-muted-foreground">Mileage out: <b className="text-midnight">{rec.mileage_out}</b></span>}
        {rec.fuel_level && <span className="text-muted-foreground">Fuel: <b className="text-midnight">{rec.fuel_level}</b></span>}
        {(rec.checked_in_by_name || rec.handled_by_name) && <span className="text-muted-foreground">By: <b className="text-midnight">{rec.checked_in_by_name || rec.handled_by_name}</b></span>}
      </div>
      {rec.existing_damage && <p className="mt-1 text-xs text-muted-foreground">Damage: {rec.existing_damage}</p>}
      {rec.final_condition && <p className="mt-1 text-xs text-muted-foreground">Condition: {rec.final_condition}</p>}
      {rec.notes && <p className="mt-1 text-xs text-muted-foreground">Notes: {rec.notes}</p>}
      {(rec.photos || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {rec.photos.map((p) => (
            <a key={p.id} href={`${API_ORIGIN}${p.url}`} target="_blank" rel="noreferrer" data-testid={`${kind}-photo-${p.id}`} className="h-16 w-16 overflow-hidden rounded-lg border border-border">
              <img src={`${API_ORIGIN}${p.url}`} alt="" className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function VF({ label, v, on, t }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">{label}</span>
      <input data-testid={t} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-brand" />
    </label>
  );
}
